import { useEffect, useState } from 'react';
import styled from 'styled-components';
import ReactTooltip from 'react-tooltip';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useTranslation } from 'react-i18next';

import {
  Title,
  Label,
  UrlInput,
  UrlPrefix,
  UrlWrapper,
  Information,
  ToggleDiv,
  SmallLabel,
  Box,
} from '../../../boardSetup/components/StyledComponents';
import ToggleSwitch from '../../../../shared/FormStyles/ToggleSwitch.jsx';
import Tooltip from '../../../../shared/Tooltip';
import { updatePublicBoard } from './data';
import SharingQrCode from '../../../../shared/SharingQrCode';

const BoardUrlLabel = styled(Box)`
  margin: 0 0 8px;
`;

const SharingLabel = styled(Label)`
  margin: 0;
`;

const CopyLink = styled(SmallLabel)`
  font-weight: 500;
  margin: 0 10px 0 0;
`;

const URL_PREFIX = `${import.meta.env.VITE_APP_HOST}/board/`;

const ShareLinkForm = ({ demo }) => {
  const queryClient = useQueryClient();

  const [copyLinkClicked, setCopyLinkClicked] = useState(false);
  const [isPublic, setIsPublic] = useState(demo.isPublic);
  const demoId = demo.uuid;

  useEffect(() => {
    ReactTooltip.rebuild();
  });

  const { mutate: publicBoardMutation } = useMutation(updatePublicBoard, {
    onSuccess: () => {
      queryClient.invalidateQueries(['getBoard']);
    },
  });

  useEffect(() => {
    publicBoardMutation({ isPublic, demoId });
  }, [demoId, isPublic, publicBoardMutation]);

  const { t } = useTranslation(['boardSetup', 'shared']);

  const tooltipContent = copyLinkClicked
    ? t('sharing.copied')
    : t('sharing.clickToCopy');

  const boardUrl = `https://${URL_PREFIX}${demo.publicSlug}`;

  return (
    <div>
      <Tooltip
        id="copyLink"
        getContent={() => tooltipContent}
        afterHide={() => setCopyLinkClicked(false)}
      />
      <Box>
        <Title>{t('sharing.title')}</Title>
      </Box>
      <BoardUrlLabel>
        <SharingLabel htmlFor="demo_url">
          {t('formLabel.publicSlug')}
        </SharingLabel>
        <Tooltip />
        <Information
          className="fa-regular fa-circle-info fa-xs"
          size={13}
          data-tip={t('sharing.shareBoardDataTip')}
          data-offset="{ 'right': 100 }"
        />
        <ToggleDiv>
          <ToggleSwitch
            id="public-board"
            name="isPublic"
            label={t('sharing.public')}
            value={isPublic}
            onChange={e => {
              setIsPublic(e.target.checked);
            }}
          />
        </ToggleDiv>
      </BoardUrlLabel>
      <UrlWrapper>
        <UrlPrefix>{URL_PREFIX}</UrlPrefix>
        <UrlInput
          id="demo_url"
          type="text"
          name="publicSlug"
          value={demo.publicSlug || ''}
          readOnly
        />
        {demo.isPublic && (
          <CopyToClipboard text={boardUrl}>
            <CopyLink
              data-for="copyLink"
              data-tip
              onClick={() => setCopyLinkClicked(true)}
            >
              {t('sharing.copyLink')}
            </CopyLink>
          </CopyToClipboard>
        )}
      </UrlWrapper>
      {demo.isPublic && (
        <SharingQrCode
          boardName={demo.name}
          isDemoPublic={isPublic}
          boardUrl={boardUrl}
        />
      )}
    </div>
  );
};

export default ShareLinkForm;
